'use client';

import { useLocale, useTranslations } from 'next-intl';
import { useEffect, useState } from 'react';
import { AnimatedButton } from '@/components/ui/animated-button';
import { Icon } from '@/components/ui/icon';
import { Globe02Icon, Tick02Icon } from '@/components/ui/icon/glyphs';
// keeps raw Input because: dialog-internal search filter; no label/description/error
import { InputField } from '@/components/ui/input-field';
import { ModalFooter } from '@/components/ui/modal-footer';
import { SilkSheet } from '@/components/ui/silk-sheet';
import { TOAST_IDS } from '@/config/toast-ids';
import { onEvent } from '@/events';
import type { Locale } from '@/i18n/config';
import { locales } from '@/i18n/config';
import { setCookie } from '@/lib/config/cookies';
import { showToast } from '@/lib/ui/toast';
import { useLocaleStore } from '@/stores/locale';
import { LOCALE_NAMES } from './locale-names';

export interface LanguageSwitcherProps {
  /** When set, dialog is controlled by parent; no trigger button is rendered. */
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}

function persistLocale(locale: Locale): void {
  setCookie('NEXT_LOCALE', locale, 365 * 24 * 60 * 60);
  localStorage.setItem('preferred-locale', locale);
}

/**
 * Headless selection state for the language picker. Owned by the wrapper that
 * renders the SilkSheet footer (standalone `LanguageSwitcher` or the combined
 * `LocaleLocationModal`) so the Update action lives in `<SilkSheet footer>` —
 * matching the form/info modal pattern.
 */
export interface LanguageSelectionState {
  currentLocale: Locale;
  selected: Locale;
  setSelected: (l: Locale) => void;
  search: string;
  setSearch: (s: string) => void;
  hasChange: boolean;
  /** Applies the selection (cookie + store + toast + event), then calls onClose. */
  apply: (onClose: () => void) => void;
}

export function useLanguageSelection(): LanguageSelectionState {
  const currentLocale = useLocale() as Locale;
  const t = useTranslations('language');
  const setLocale = useLocaleStore((s) => s.setLocale);
  const [search, setSearch] = useState<string>('');
  const [selected, setSelected] = useState<Locale>(currentLocale);

  function apply(onClose: () => void): void {
    if (selected === currentLocale) {
      onClose();
      return;
    }
    persistLocale(selected);
    setLocale(selected);
    onEvent('locale_changed', { from_locale: currentLocale, to_locale: selected });
    showToast({
      id: TOAST_IDS.LOCALE_CHANGED,
      message: t('success'),
      variant: 'success',
    });
    setSearch('');
    onClose();
  }

  return {
    currentLocale,
    selected,
    setSelected,
    search,
    setSearch,
    hasChange: selected !== currentLocale,
    apply,
  };
}

export interface LanguageSwitcherContentProps {
  state: LanguageSelectionState;
}

/** Controlled body for language selection (no Dialog, no action button). */
export function LanguageSwitcherContent({ state }: LanguageSwitcherContentProps): React.ReactNode {
  const t = useTranslations('language');
  const { selected, setSelected, search, setSearch } = state;

  const filtered =
    search.length === 0
      ? locales
      : locales.filter((l) => {
          const q = search.toLowerCase();
          return LOCALE_NAMES[l].toLowerCase().includes(q) || l.toLowerCase().includes(q);
        });

  return (
    <>
      <InputField
        placeholder={t('search_placeholder')}
        value={search}
        onChange={(e: React.ChangeEvent<HTMLInputElement>): void => {
          setSearch(e.target.value);
        }}
        containerClassName="my-2"
      />

      <div className="h-64 overflow-y-auto rounded-control border bg-background-primary">
        {filtered.length === 0 && (
          <p className="flex h-full items-center justify-center px-4 text-center text-body text-foreground-secondary">
            {t('no_results')}
          </p>
        )}
        {filtered.map((l) => {
          const isSelected = l === selected;
          return (
            <button
              key={l}
              type="button"
              className="flex w-full items-center gap-3 px-3 py-2 text-left text-body transition-colors hover:bg-background-tertiary"
              onClick={(): void => {
                setSelected(l);
              }}
            >
              <span className="flex-1">{LOCALE_NAMES[l]}</span>
              {isSelected && (
                <Icon
                  icon={Tick02Icon}
                  className="size-4 text-foreground-primary"
                  aria-hidden="true"
                />
              )}
            </button>
          );
        })}
      </div>
    </>
  );
}

export function LanguageSwitcher({
  open: controlledOpen,
  onOpenChange: controlledOnOpenChange,
}: LanguageSwitcherProps = {}): React.ReactNode {
  const t = useTranslations('language');
  const [internalOpen, setInternalOpen] = useState<boolean>(false);
  const state = useLanguageSelection();

  const isControlled = controlledOnOpenChange !== undefined;
  const open = isControlled ? (controlledOpen ?? false) : internalOpen;
  const setOpen = isControlled
    ? (controlledOnOpenChange ?? ((_v: boolean): void => {}))
    : setInternalOpen;

  // Reset the staged selection back to the current locale whenever the modal
  // reopens — avoids "Update" being primed from a previous open's selection.
  const { setSelected, currentLocale } = state;
  useEffect(() => {
    if (open) setSelected(currentLocale);
  }, [open, setSelected, currentLocale]);

  function handleOpenChange(isOpen: boolean): void {
    setOpen(isOpen);
  }

  return (
    <SilkSheet
      open={open}
      onOpenChange={handleOpenChange}
      variant="detached"
      size="md"
      keyboard
      title={t('title')}
      trigger={
        !isControlled ? (
          <AnimatedButton variant="tertiary" prefix={Globe02Icon} aria-label={t('label')} />
        ) : undefined
      }
      footer={
        <ModalFooter>
          <AnimatedButton
            onClick={(): void => state.apply((): void => setOpen(false))}
            disabled={!state.hasChange}
          >
            {t('update_button')}
          </AnimatedButton>
        </ModalFooter>
      }
    >
      <LanguageSwitcherContent state={state} />
    </SilkSheet>
  );
}
